
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Bill {
  id: string;
  due_date: string;
  status: string;
  product_name: string;
  plan_name: string;
}

export function useSubscriptionsStats() {
  const [activeProducts, setActiveProducts] = useState<string[]>([]);
  const [expiredProducts, setExpiredProducts] = useState<string[]>([]);
  const [unpaidProducts, setUnpaidProducts] = useState<string[]>([]);
  const [nextBill, setNextBill] = useState<Bill | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        const { data: schoolUser } = await supabase
          .from('school_users')
          .select('school_id')
          .eq('email', user.email)
          .maybeSingle();

        if (!schoolUser) {
          setLoading(false);
          return;
        }

        const { data: client } = await supabase
          .from('clients')
          .select('id')
          .eq('school_id', schoolUser.school_id)
          .maybeSingle();

        if (!client) {
          setLoading(false);
          return;
        }

        // Fetch subscriptions with product and plan information
        const { data: subscriptions, error: subscriptionsError } = await supabase
          .from('subscriptions')
          .select(`
            id,
            status,
            start_date,
            end_date,
            plan:plans (
              name,
              product:products (
                name
              )
            )
          `)
          .eq('client_id', client.id);

        if (subscriptionsError) throw subscriptionsError;

        // Process subscriptions to categorize products
        const active: string[] = [];
        const expired: string[] = [];

        subscriptions?.forEach(sub => {
          const productName = sub.plan?.product?.name;
          if (!productName) return;
          
          const now = new Date();
          const isExpired = sub.end_date && new Date(sub.end_date) < now;
          const isTerminated = sub.status === 'terminated';
          const isCancelled = sub.status === 'cancelled';
          
          if (isExpired || isTerminated || isCancelled) {
            expired.push(productName);
          } else if (sub.status === 'active') {
            active.push(productName);
          }
        });

        // Fetch unpaid bills with product name information
        const { data: bills, error: billsError } = await supabase
          .from('bills')
          .select(`
            id,
            status,
            due_date,
            plan:plans (
              name,
              product:products (
                name
              )
            )
          `)
          .eq('school_id', schoolUser.school_id)
          .eq('status', 'unpaid')
          .order('due_date', { ascending: true });

        if (billsError) throw billsError;

        // Process bills to get unpaid products and next bill
        const unpaid: string[] = [];
        let nextDueBill: Bill | null = null;

        bills?.forEach(bill => {
          const productName = bill.plan?.product?.name;
          if (!productName) return;
          
          // Add to unpaid products if not already there
          if (!unpaid.includes(productName)) {
            unpaid.push(productName);
          }
          
          // Find next due bill (first unpaid bill)
          if (!nextDueBill && bill.status === 'unpaid') {
            nextDueBill = {
              id: bill.id,
              due_date: bill.due_date,
              status: bill.status,
              product_name: productName,
              plan_name: bill.plan.name
            };
          }
        });

        setActiveProducts(active);
        setExpiredProducts(expired);
        setUnpaidProducts(unpaid);
        setNextBill(nextDueBill);
      } catch (error) {
        console.error('Error fetching subscription stats:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch subscription statistics",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [toast]);

  return {
    activeProducts,
    expiredProducts,
    unpaidProducts,
    nextBill,
    loading
  };
}
